/**
 * Soft celebration tokens — aligned with dashboard cards, not marketing heroes.
 * Seller: slate neutrals + whisper purple/indigo (gamification cards).
 * Creator: gray-50 studio + soft fuchsia accents (Creator Studio pages).
 */

export type CelebrationPersona = 'seller' | 'creator';

export const CELEBRATION_OVERLAY =
  'bg-slate-900/20 backdrop-blur-[2px]';

export const CELEBRATION_PANEL: Record<CelebrationPersona, string> = {
  seller:
    'overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-lg ring-1 ring-slate-900/[0.04]',
  creator:
    'overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg ring-1 ring-gray-900/[0.03]',
};

/** Soft wash behind icon + title — same weight as gamification elevated cards. */
export const CELEBRATION_HEADER: Record<CelebrationPersona, string> = {
  seller:
    'relative border-b border-slate-100 bg-gradient-to-br from-purple-50/40 via-white to-indigo-50/20 px-6 py-7 text-center',
  creator:
    'relative border-b border-gray-100 bg-gradient-to-br from-fuchsia-50/30 via-white to-violet-50/15 px-6 py-7 text-center',
};

export const CELEBRATION_ACCENT_BAR: Record<CelebrationPersona, string> = {
  seller: 'h-1 bg-gradient-to-r from-purple-300/70 via-indigo-300/60 to-violet-300/50',
  creator: 'h-1 bg-gradient-to-r from-fuchsia-300/70 via-violet-300/55 to-purple-300/45',
};

export const CELEBRATION_EYEBROW: Record<CelebrationPersona, string> = {
  seller: 'text-[11px] font-semibold uppercase tracking-[0.08em] text-purple-700',
  creator: 'text-[11px] font-semibold uppercase tracking-[0.08em] text-fuchsia-600',
};

export const CELEBRATION_TITLE = 'mt-1.5 text-lg font-semibold tracking-tight text-slate-900';

export const CELEBRATION_BODY = 'text-center text-sm leading-relaxed text-slate-600';

export const CELEBRATION_META = 'mt-2 text-center text-xs text-slate-400';

export const CELEBRATION_CLOSE =
  'rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600';

export const CELEBRATION_XP_CHIP: Record<CelebrationPersona, string> = {
  seller: 'bg-purple-50 text-purple-800 ring-purple-200/70',
  creator: 'bg-fuchsia-50 text-fuchsia-800 ring-fuchsia-200/60',
};

export const CELEBRATION_QUEUE_BADGE: Record<CelebrationPersona, string> = {
  seller: 'bg-slate-100 text-slate-600 ring-slate-200/80',
  creator: 'bg-gray-100 text-gray-600 ring-gray-200/80',
};

export const CELEBRATION_PRIMARY_CTA: Record<CelebrationPersona, string> = {
  seller:
    'inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800',
  creator:
    'inline-flex w-full items-center justify-center rounded-xl bg-fuchsia-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-fuchsia-700',
};

export const CELEBRATION_SECONDARY_CTA: Record<CelebrationPersona, string> = {
  seller:
    'inline-flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:border-purple-200 hover:bg-purple-50/50',
  creator:
    'inline-flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:border-fuchsia-200 hover:bg-fuchsia-50/60',
};

export const CELEBRATION_CONTEXT_LABEL: Record<CelebrationPersona, string> = {
  seller: 'Seller progress',
  creator: 'Creator Studio',
};
